/**
 * Simple Validation Mode
 *
 * Each widget has a predetermined correct answer.
 * Validation checks if user input matches the expected answer.
 */
import type { ValidatedExerciseJSON, NumberLineItem, LayoutItem } from '../transformers/json-schema';
import { isWidgetRef } from '../transformers/json-schema';
import { ValidationResult, WidgetValidation } from './types';
import { isNLDragCorrect, isNLDragEncoded } from '../utils/number-line.utils';
import { JSON_TYPES } from '../constants';
import { normalize } from '../utils';
import { create, all, MathJsStatic, FactoryFunctionMap } from 'mathjs';
import { areMatcherEqual } from './widget-validators/matcher';
import { getPairMatcherGroupResults } from './widget-validators/pair-matcher';
import { areVisualFractionCountsEqual } from './widget-validators/visual-fraction';

const math = create(all as FactoryFunctionMap) as MathJsStatic;

// Recursively collects all NumberLine items, including those nested inside GROUP blocks.
function collectNumberLines(items: LayoutItem[]): NumberLineItem[] {
  const result: NumberLineItem[] = [];
  for (const item of items) {
    if (item.type === JSON_TYPES.NUMBER_LINE) result.push(item);
    else if (item.type === JSON_TYPES.GROUP) result.push(...collectNumberLines(item.items));
  }
  return result;
}

// Builds widgetId → axis position for every POSITION slot across all number lines.
function buildPositionMap(exercise: ValidatedExerciseJSON): Record<string, number> {
  const map: Record<string, number> = {};
  for (const nl of collectNumberLines(exercise.layout)) {
    for (const pos of nl.positions) {
      for (const c of pos.content) {
        // One widget per POSITION is enforced at parse time (src/ast/number-line.ts:44).
        // The break is intentional — there can never be more than one ref here.
        if (isWidgetRef(c)) { map[c.widgetId] = pos.value; break; }
      }
    }
  }
  return map;
}

/**
 * Validate using simple mode (predetermined answers)
 *
 * @param exercise - The exercise JSON (with validation present)
 * @param userInputs - User's answers keyed by widget ID
 * @returns Validation result
 */
export function validateSimple(
  exercise: ValidatedExerciseJSON,
  userInputs: Record<string, string | number | string[][] | number[]>
): ValidationResult {
  const details: Record<string, WidgetValidation> = {};
  let allCorrect = true;
  let correctCount = 0;
  let totalCount = 0;

  // Check that we have answers to validate against
  const answers = exercise.validation.answers;
  if (!answers) {
    return {
      isCorrect: false,
      details: {},
      score: 0,
      error: 'No answers defined for simple validation'
    };
  }

  // widgetId → axis value for every POSITION[value=…] slot (used for drag validation)
  const positionMap = buildPositionMap(exercise);

  // Validate each widget that has an expected answer
  for (const [widgetId, expected] of Object.entries(answers)) {
    // Matcher widget — use dedicated order-insensitive comparator.
    const widget = exercise.widgets[widgetId];
    if (widget?.type === JSON_TYPES.MATCHER) {
      const userValue = userInputs[widgetId] as string[][] | undefined;
      const isCorrect = areMatcherEqual(expected as string[][], userValue);
      details[widgetId] = { widgetId, userValue, isCorrect };
      totalCount++;
      if (isCorrect) correctCount++;
      else allCorrect = false;
      continue;
    }

    // Pair matcher — isCorrect derived from groupResults (single validation pass).
    if (widget?.type === JSON_TYPES.PAIR_MATCHER) {
      const userValue = userInputs[widgetId] as string[][] | undefined;
      const groupResults = getPairMatcherGroupResults(expected as string[][], userValue);
      const isCorrect = groupResults.length === (expected as string[][]).length
        && groupResults.every(r => r.isCorrect);
      details[widgetId] = {
        widgetId,
        userValue,
        isCorrect,
        groupResults,
      };
      totalCount++;
      if (isCorrect) correctCount++;
      else allCorrect = false;
      continue;
    }

    // VISUAL_FRACTION widget — compare count arrays positionally
    if (widget?.type === JSON_TYPES.VISUAL_FRACTION) {
      const userValue = userInputs[widgetId] as number[] | undefined;
      const isCorrect = areVisualFractionCountsEqual(expected as number[], userValue);
      details[widgetId] = { widgetId, userValue, isCorrect };
      totalCount++;
      if (isCorrect) correctCount++;
      else allCorrect = false;
      continue;
    }

    totalCount++;
    const userValue = userInputs[widgetId];

    // Check if this is a fraction widget with simplified=false (lenient mode)
    const isFractionLenient = widget?.type === JSON_TYPES.FRACTION
      && (widget as any).simplified === false;

    const isCorrect = isFractionLenient
      ? areFractionsEquivalent(userValue as string | number | undefined, expected as string | number)
      : areValuesEqual(userValue as string | number | undefined, expected as string | number, positionMap[widgetId]);

    details[widgetId] = {
      widgetId,
      userValue,
      isCorrect
    };

    if (isCorrect) {
      correctCount++;
    } else {
      allCorrect = false;
    }
  }

  // Check for any widgets the user provided input for but we don't have answers for
  // (this shouldn't happen in normal use, but we track it for debugging)
  for (const widgetId of Object.keys(userInputs)) {
    if (!(widgetId in answers) && !(widgetId in details)) {
      details[widgetId] = {
        widgetId,
        userValue: userInputs[widgetId],
        isCorrect: true // No validation rule = assume correct
      };
    }
  }

  return {
    isCorrect: allCorrect,
    details,
    score: totalCount > 0 ? correctCount / totalCount : 0
  };
}

function areValuesEqual(
  userValue: string | number | undefined,
  expected: string | number,
  expectedPosition?: number
): boolean {
  if (userValue === undefined || userValue === null) return false;

  if (typeof userValue === 'string' && isNLDragEncoded(userValue)) {
    // Drag value without a declared POSITION slot is a DSL misconfiguration → reject.
    if (expectedPosition === undefined) return false;
    return isNLDragCorrect(userValue, expected, expectedPosition);
  }

  return normalize(userValue) === normalize(expected);
}

/**
 * Compare two fraction strings using mathjs fraction equivalence.
 * "10/6" and "5/3" are equivalent because math.equal(fraction(10,6), fraction(5,3)) is true.
 * Falls back to string equality if parsing fails.
 */
function areFractionsEquivalent(
  user: string | number | undefined,
  expected: string | number
): boolean {
  if (user === undefined || user === null) return false;

  try {
    const userFrac = math.fraction(String(user));
    const expectedFrac = math.fraction(String(expected));
    return math.equal(userFrac, expectedFrac) as boolean;
  } catch {
    // Fallback to string comparison if mathjs can't parse
    return areValuesEqual(user, expected);
  }
}
